/**
 * 작성 화면(new-composed 편집 모드)의 형제 칩 목적지 해소.
 *
 * 상세의 형제 칩은 언제나 상세(`/reports/:id`)로 열지만, 작성 화면에서는 **작성이 남은
 * 형제를 편집 화면으로** 열어야 "저장 → 다음 그룹" 순회가 끊기지 않는다(결정 2026-09-01 §④의
 * "편집 불가 문서는 보기로 열린다"를 작성 화면 쪽에서 뒤집어 적은 것).
 *
 * ⚠️ 판정은 **클라이언트가 아는 축만** 쓴다 — CREATING(초안 전 단계) + 작성자 본인.
 * 분할 산물은 CREATING으로 태어나고 작성 완료 저장이 DRAFT로 승격하므로(2026-09-06 상태
 * 모델), "이어 쓸 문서"는 곧 CREATING이다. DRAFT는 이미 작성을 마친 문서라 상세로 연다 —
 * 완료한 문서를 다시 "이어 작성"으로 권하는 순환이 이 축 하나로 사라진다.
 * 서버 정본은 `GET /reports/:id/can-edit`이지만 칩마다 왕복을 걸 수는 없고(칩은 N개다),
 * 걸어도 답이 늦게 와 칩이 뒤늦게 바뀐다.
 *
 * 오판이 막다른 길을 만들지 않는 것이 이 근사의 전제다: 편집 URL로 들어가도 new-composed가
 * 진입 시 `canEditReport`를 물어 편집 불가면 상세로 `replace`한다. 즉 최악이 "한 번 튕겨
 * 상세로 간다"이지 빈 화면이 아니다. 반대 방향(편집 가능한데 상세로 보냄)도 상세에 '수정'
 * 버튼이 있어 한 번 더 누르면 된다.
 *
 * DRAFT·REJECTED를 제외하는 이유는 위와 같다 — DRAFT는 작성 완료본(상세가 정위치),
 * REJECTED는 재제출 편집 권한(`editableScope === 'all'`)을 서버만 알아 상태만으로 단정하면
 * 권한 없는 사람이 매번 튕긴다.
 */
import type { Report } from '../../lib/reports';

/**
 * composed 편집 화면 URL — 작성 이어가기(CREATING)와 검수 순회(postSubmitFlows의 검수 축,
 * S33/F-3)가 **같은 목적지**를 쓴다. 문자열을 각자 조립하면 쿼리 키(editReportId) 하나만
 * 어긋나도 한쪽이 조용히 새 문서 생성 화면이 된다.
 */
export function composedEditHref(templateKey: string, reportId: string): string {
  return `/reports/new/composed/${encodeURIComponent(templateKey)}?editReportId=${reportId}`;
}

/**
 * 이 형제가 **아직 작성을 마치지 않아 작성 화면에서 이어 쓸 수 있는가**(CREATING + 본인).
 * 목적지 판정(아래)과 "남은 문서가 있는가" 판정이 같은 축을 써야 화면이 갈리지 않아 술어로
 * 뺐다 — 토스트가 "1건 남았습니다"라고 말했는데 그 문서로 갈 링크가 상세라면 안내가 거짓이
 * 된다.
 *
 * @param sibling 판정 대상 문서
 * @param userId 현재 사용자 id(미로그인/로딩 중이면 undefined — 그 경우 전부 편집 불가로 본다)
 */
export function isSiblingAwaitingWrite(sibling: Report, userId?: string): boolean {
  if (sibling.status !== 'CREATING') return false;
  return Boolean(userId) && sibling.authorId === userId;
}

/**
 * @param sibling 목적지 문서
 * @param userId 현재 사용자 id(미로그인/로딩 중이면 undefined — 그 경우 전부 상세로)
 */
export function resolveSiblingWriteHref(sibling: Report, userId?: string): string {
  if (!isSiblingAwaitingWrite(sibling, userId)) {
    return `/reports/${sibling.id}`;
  }
  return composedEditHref(sibling.templateKey, sibling.id);
}
